import styled, { css } from 'styled-components';

import { isJobTrackEnable, normalizeLocale } from '/lib/helpers';
import Label from '/imports/core/ui/atoms/Label';
import { TipIcon } from '/imports/generator/ui/assets';
import useIntl from '/imports/core/api/useIntl';
import { useResponsive } from '/imports/core/api/responsiveContext';
import { isGeneratorPhoneTooltip } from 'imports/experiment/GeneratorPhoneTooltipExp';

const FieldLabel = (props) => {
  const {
    label = '',
    tip,
    zIndex,
    exposed,
    summary,
    longTitle,
    background = '#f7f7fc',
    isStartForm = false,
    htmlFor,
    smallFont = false,
    accountPage = false,
    customLongTitle = false,
    isCoverLetter,
    language,
    id = undefined,
    color,
    isDownloadPopup = false,
    largeFont = false,
    isPreviewLayout = false,
    style,
    name = '',
  } = props;
  const { locale, t } = useIntl();
  const { host } = useResponsive();
  const safeLocale = normalizeLocale(locale);
  const withPhoneTooltip = name === 'phone' && isGeneratorPhoneTooltip(host);
  return (
    <StyledLabel
      language={language}
      zIndex={Number(!!zIndex)}
      exposed={Number(!!exposed)}
      summary={Number(!!summary)}
      longTitle={Number(!!longTitle)}
      background={background}
      smallFont={smallFont || locale == 'ka'}
      isStartForm={isStartForm}
      htmlFor={htmlFor}
      accountPage={accountPage}
      customLongTitle={customLongTitle}
      isCoverLetter={isCoverLetter}
      locale={locale}
      id={id}
      color={color}
      isDownloadPopup={isDownloadPopup}
      largeFont={largeFont}
      isPreviewLayout={isPreviewLayout}
      style={style}
    >
      {isJobTrackEnable() ? <span>{label}</span> : <span>{label.toLocaleUpperCase(safeLocale)}</span>}
      {tip && <TipIcon tip={tip} />}
      {withPhoneTooltip && (
        <InfoButton
          type="button"
          className="info-dot"
          aria-label="Informations sur la confidentialité"
          aria-describedby="privacy-tip"
          data-tooltip-id="privacy-tip"
          data-tooltip-place="top"
          data-tooltip-offset="12"
        >
          i
        </InfoButton>
      )}

      <OverlayBackground />
    </StyledLabel>
  );
};

export const InfoButton = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #0e1016;
  color: #ffffff;
  font-size: 12px;
  line-height: 1;
  cursor: default;
  user-select: none;
  text-transform: lowercase;
  margin-left: 5px;
  font-family: ${({ theme }) => theme.font.family.websiteMedium};
  &:hover,
  &:focus-visible {
    transform: scale(1.04);
    outline: none;
  }
`;

const StyledLabel = styled(Label)`
  padding: 0 5px;
  position: absolute;
  top: ${({ theme }) => (theme.designV2 ? '-9px' : '-8px')};
  left: ${({ exposed }) => (exposed ? '27px' : '27.5px')};
  z-index: ${({ zIndex }) => (zIndex ? 153 : 10)};
  font-family: ${({ theme }) => theme.font.family.websiteSemiBold};
  font-size: 11px;
  letter-spacing: 0.2px;
  text-align: center;
  background: ${({ background }) => background};
  color: #46466b;
  text-transform: uppercase;
  ${({ isCoverLetter }) =>
    isCoverLetter &&
    css`
      z-index: 9;
    `}

  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `}
  @media (min-width: 1025px) and (max-width: 1300px) {
    ${({ customLongTitle }) =>
      customLongTitle &&
      css`
        font-size: 6px !important;
        left: 25px !important;
      `}
  }

  @media (min-width: 1301px) {
    ${({ customLongTitle }) =>
      customLongTitle &&
      css`
        font-size: 8px !important;
        left: 25px !important;
      `}
  }

  @media (min-width: 1025px) and (max-width: 1455px) {
    ${({ theme: { isRTL }, customLongTitle }) =>
      customLongTitle &&
      css`
        text-align: ${isRTL ? 'right' : 'left'};
      `}
  }

  ${({ theme }) =>
    theme.isJobTrack &&
    css`
      color: var(--dark-values-dark-regular) !important;
      line-height: 14px !important;
      top: -6px !important;
      right: '30px' !important;
      left: 'auto' !important;
      background-color: #fff;
      text-transform: capitalize !important;
      font-size: 11px !important;
      font-family: ${({ theme }) => theme.font.family.websiteSemiBold} !important;
    `}
  ${({ accountPage }) =>
    accountPage &&
    css`
      top: -2px !important;
    `}
  ${({ theme }) =>
    theme.designV2 &&
    css`
      padding: 0 4.6px;
    `}

  ${({ theme: { isRTL }, exposed }) =>
    isRTL &&
    css`
      left: auto;
      right: ${exposed ? '30px' : '31px'};
    `}
  span {
    position: relative;
    z-index: 10;
  }

  ${({ theme, summary, isDownloadPopup }) =>
    summary &&
    css`
      left: ${isDownloadPopup ? '8px' : !theme.isRTL ? '15px' : 'auto'};
      right: ${theme.isRTL ? '15px' : 'auto'};
      left: ${isDownloadPopup && theme.isRTL && 'unset'};
    `}

  ${({ longTitle }) =>
    longTitle &&
    css`
      min-height: 20px;
      ${({ theme }) => theme.max('xs')`
      width: calc(100% - 61px);
      bottom: calc(100% - 23px);
      line-height: 15px;
      background-color: transparent;

      div {
        top: auto;
        bottom: 3px;
        height: 12px;
      }
    `}
      ${({ theme, isCoverLetter }) => theme.max('sm')`
        font-size: 9px;
        text-align: start;
        ${!isCoverLetter ? 'width: auto;' : ''}
      `}
    `}

    ${({ smallFont }) =>
    smallFont &&
    css`
      @media (min-width: 1025px) and (max-width: 1420px) {
        font-size: 6px !important;
      }
      @media (min-width: 801px) and (max-width: 900px) {
        font-size: 9px !important;
      }
      @media (max-width: 425px) {
        font-size: 9px !important;
      }
      @media (min-width: 325px) and (max-width: 360px) {
        font-size: 8px !important;
      }
      @media (max-width: 324px) {
        font-size: 6px !important;
      }
    `}
  ${({ largeFont }) =>
    largeFont &&
    css`
      color: #4c4c55;
      font-family: Gilroy Bold;
      font-size: 12px;
      font-weight: 500;
      text-transform: uppercase;
    `}

    ${({ isPreviewLayout }) => css`
    ${isPreviewLayout &&
    css`
      color: #4c4c55 !important;
      font-family: ${({ theme }) => theme.font.family.websiteMedium} !important;
      font-size: 12px !important;
      text-transform: uppercase !important;
    `}
  `}
`;

const OverlayBackground = styled.div`
  background: white;
  width: 100%;
  position: absolute;
  top: 10px;
  height: 10px;
  left: 0;
`;

export default FieldLabel;
